import React from 'react';
import { useRiskStats, useTaskStats } from '../../hooks/useDashboardQueries';
import { LedgerSkeleton } from '../shell/LedgerSkeleton';
import { cn } from '../../lib/cn';

interface Kpi {
  label: string;
  value: string;
  secondary: string;
  alert?: boolean;
}

const Figure: React.FC<{ kpi: Kpi }> = ({ kpi }) => (
  <div className="min-w-0 py-2">
    <div className="cap-label">{kpi.label}</div>
    <div className={cn('font-serif text-scale-6 font-medium', kpi.alert ? 'text-sev-critical' : 'text-ink')}>
      {kpi.value}{' '}
      <span className="font-sans text-scale-1 font-normal text-ink-muted">{kpi.secondary}</span>
    </div>
  </div>
);

export const KpiStrip: React.FC = () => {
  const risks = useRiskStats();
  const tasks = useTaskStats();

  if (risks.isPending || tasks.isPending) {
    return <LedgerSkeleton lines={2} className="my-4" data-testid="kpi-skeleton" />;
  }
  if (!risks.data || !tasks.data) return null;

  const openTasks = tasks.data.totalTasks - tasks.data.completedTasks;
  const kpis: Kpi[] = [
    { label: 'Active risks', value: String(risks.data.openRisks), secondary: `of ${risks.data.totalRisks} on file` },
    { label: 'Overdue tasks', value: String(tasks.data.overdueTasks), secondary: `of ${openTasks} open`, alert: tasks.data.overdueTasks > 0 },
    { label: 'Review overdue', value: String(risks.data.overdueRisks), secondary: 'risks past review' },
    { label: 'Avg. residual', value: risks.data.averageRiskScore.toFixed(1), secondary: '/ 25' },
  ];

  return (
    <section aria-label="Key figures"
      className="mb-5 grid grid-cols-2 gap-x-5 border-y border-rule-faint md:flex md:divide-x md:divide-rule-faint md:gap-0">
      {kpis.map((k, i) => (
        <div key={k.label} className={cn('md:flex-1', i > 0 && 'md:pl-5', i >= 2 && 'border-t border-rule-faint md:border-t-0')}>
          <Figure kpi={k} />
        </div>
      ))}
    </section>
  );
};
